import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
    Container,
    Paper,
    TextField,
    Button,
    Typography,
    Box,
    Alert,
    CircularProgress,
    Link,
    useTheme,
    alpha
} from '@mui/material';
import { authService } from '../../services/authService';
import { extractApiErrorMessage } from '../../utils/apiUtils';
import { DEFAULT_THEME } from '../../constants/themeDefaults';
import LoginLayout from '../../components/layout/LoginLayout';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

export const ForgotPassword: React.FC = () => {
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [emailSent, setEmailSent] = useState(false);
    const navigate = useNavigate();
    const theme = DEFAULT_THEME;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setMessage(null);

        try {
            const result = await authService.sendPasswordReset(email);
            
            if (result.success) {
                setMessage({ type: 'success', text: result.message });
                setEmailSent(true);
            } else {
                setMessage({ type: 'error', text: result.message });
            }
        } catch (error) {
            setMessage({ type: 'error', text: extractApiErrorMessage(error, 'An unexpected error occurred. Please try again.') });
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendEmail = async () => {
        setIsLoading(true);
        setMessage(null);

        try {
            const result = await authService.sendPasswordReset(email);
            
            if (result.success) {
                setMessage({ type: 'success', text: 'Password reset email sent again!' });
            } else {
                setMessage({ type: 'error', text: result.message });
            }
        } catch (error) {
            setMessage({ type: 'error', text: extractApiErrorMessage(error, 'An unexpected error occurred. Please try again.') });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <LoginLayout>
            <Container component="main" maxWidth="sm">
                <Box
                    sx={{
                        marginTop: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Paper
                        elevation={3}
                        sx={{
                            padding: 4,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            backgroundColor: alpha(theme.backgroundColor, 0.9),
                            backdropFilter: 'blur(10px)',
                            borderRadius: 3,
                            border: `1px solid ${alpha(theme.primaryColor, 0.2)}`,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 2,
                            }}
                        >
                            <CalendarMonthIcon
                                sx={{
                                    fontSize: 40,
                                    color: theme.primaryColor,
                                    mr: 1,
                                }}
                            />
                            <Typography
                                component="h1"
                                variant="h4"
                                sx={{
                                    color: theme.textColor,
                                    fontWeight: 'bold',
                                }}
                            >
                                MindShare
                            </Typography>
                        </Box>

                        <Typography
                            component="h2"
                            variant="h5"
                            sx={{
                                color: theme.textColor,
                                mb: 3,
                                textAlign: 'center',
                            }}
                        >
                            {emailSent ? 'Check Your Email' : 'Forgot Your Password?'}
                        </Typography>

                        {!emailSent ? (
                            <>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: theme.textColor,
                                        mb: 3,
                                        textAlign: 'center',
                                        opacity: 0.8,
                                    }}
                                >
                                    Enter your email address and we'll send you a link to reset your password.
                                </Typography>

                                <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
                                    {message && (
                                        <Alert
                                            severity={message.type}
                                            sx={{ mb: 2, whiteSpace: 'pre-line' }}
                                        >
                                            {message.text}
                                        </Alert>
                                    )}

                                    <TextField
                                        margin="normal"
                                        required
                                        fullWidth
                                        id="email"
                                        label="Email Address"
                                        name="email"
                                        autoComplete="email"
                                        autoFocus
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: alpha(theme.primaryColor, 0.3),
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: theme.primaryColor,
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: theme.primaryColor,
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: theme.textColor,
                                            },
                                            '& .MuiInputLabel-root.Mui-focused': {
                                                color: theme.primaryColor,
                                            },
                                        }}
                                    />

                                    <Button
                                        type="submit"
                                        fullWidth
                                        variant="contained"
                                        disabled={isLoading}
                                        sx={{
                                            mt: 3,
                                            mb: 2,
                                            backgroundColor: theme.primaryColor,
                                            '&:hover': {
                                                backgroundColor: alpha(theme.primaryColor, 0.8),
                                            },
                                            '&:disabled': {
                                                backgroundColor: alpha(theme.primaryColor, 0.5),
                                            },
                                        }}
                                    >
                                        {isLoading ? (
                                            <CircularProgress size={24} color="inherit" />
                                        ) : (
                                            'Send Reset Email'
                                        )}
                                    </Button>

                                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                                        <Link
                                            component={RouterLink}
                                            to="/login"
                                            variant="body2"
                                            sx={{
                                                color: theme.primaryColor,
                                                textDecoration: 'none',
                                                '&:hover': {
                                                    textDecoration: 'underline',
                                                },
                                            }}
                                        >
                                            Back to Login
                                        </Link>
                                    </Box>
                                </Box>
                            </>
                        ) : (
                            <Box sx={{ textAlign: 'center', width: '100%' }}>
                                <EmailOutlinedIcon
                                    sx={{
                                        fontSize: 64,
                                        color: theme.primaryColor,
                                        mb: 2,
                                    }}
                                />

                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: theme.textColor,
                                        mb: 3,
                                        textAlign: 'center',
                                    }}
                                >
                                    We've sent a password reset link to <strong>{email}</strong>
                                </Typography>

                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: theme.textColor,
                                        mb: 3,
                                        textAlign: 'center',
                                        opacity: 0.8,
                                    }}
                                >
                                    Check your email and click the link to reset your password. 
                                    The link will expire in 24 hours.
                                </Typography>

                                <Button
                                    onClick={handleResendEmail}
                                    disabled={isLoading}
                                    variant="outlined"
                                    sx={{
                                        mb: 2,
                                        borderColor: theme.primaryColor,
                                        color: theme.primaryColor,
                                        '&:hover': {
                                            borderColor: theme.primaryColor,
                                            backgroundColor: alpha(theme.primaryColor, 0.1),
                                        },
                                    }}
                                >
                                    {isLoading ? (
                                        <CircularProgress size={24} color="inherit" />
                                    ) : (
                                        'Resend Email'
                                    )}
                                </Button>

                                <Box sx={{ textAlign: 'center', mt: 2 }}>
                                    <Link
                                        component={RouterLink}
                                        to="/login"
                                        variant="body2"
                                        sx={{
                                            color: theme.primaryColor,
                                            textDecoration: 'none',
                                            '&:hover': {
                                                textDecoration: 'underline',
                                            },
                                        }}
                                    >
                                        Back to Login
                                    </Link>
                                </Box>
                            </Box>
                        )}
                    </Paper>
                </Box>
            </Container>
        </LoginLayout>
    );
};

export default ForgotPassword;
